/**
 * CommunityLifecycleSection — DEV-1464 / POLICY_COMMUNITY_DELETION.md, Slice 4.
 *
 * The three states (§1) made operable:
 *
 *   Dissolve    — the PROMINENT, safe action. The community stops: inert, no new
 *                 members/roles/projects/content/feed posts. History stays fully
 *                 readable, content stays published and attributed. Reversible,
 *                 so NO type-to-confirm (§6).
 *   Delete      — the QUIET one behind it. Moves to a 30-day trash. The window IS
 *                 the protection, so no type-to-confirm here either.
 *   Empty Trash — the ONE strong gate: type the community name. Irreversible.
 *
 * Dissolution is the expected end state for a real community; deletion is for
 * mistakes, tests, spam and empty shells. The visual hierarchy says so.
 *
 * GATED AFFORDANCES (CLAUDE.md): the server computes every verdict. This
 * component NEVER re-derives the guards — it renders `canDelete` and, when
 * false, disables the control and states the specific earning path from
 * `blockReasons`. Never enabled-then-fail.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import {
  getCommunityLifecycle,
  dissolveCommunity,
  reactivateCommunity,
  softDeleteCommunity,
  restoreCommunity,
  purgeCommunity,
  getDeletedCommunities,
  type CommunityLifecycleState,
  type DeletedCommunity,
} from '../../api/communityLifecycle';
import styles from './CommunityLifecycleSection.module.css';

interface Props {
  communityGuid: string;
  disabledAll?: boolean;
}

type Dialog =
  | null
  | { kind: 'dissolve' }
  | { kind: 'reactivate' }
  | { kind: 'delete' }
  | { kind: 'purge'; target: DeletedCommunity };

const CommunityLifecycleSection: React.FC<Props> = ({ communityGuid, disabledAll }) => {
  const { t } = useAppTranslation('community');

  const [state, setState] = useState<CommunityLifecycleState | null>(null);
  const [trash, setTrash] = useState<DeletedCommunity[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [confirmName, setConfirmName] = useState('');

  const load = useCallback(async () => {
    if (!communityGuid) return;
    setLoading(true);
    setError(null);
    try {
      const [s, d] = await Promise.all([
        getCommunityLifecycle(communityGuid),
        getDeletedCommunities().catch(() => [] as DeletedCommunity[]),
      ]);
      setState(s);
      setTrash(d);
    } catch (e: any) {
      setError(e?.message || t('lifecycle.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [communityGuid, t]);

  useEffect(() => { void load(); }, [load]);

  /**
   * The blocked-reason copy. The server ships machine-readable keys, never
   * English — this is where they become a sentence that tells the admin what to
   * DO, not merely that they can't.
   */
  const blockedMessage = useMemo(() => {
    if (!state || state.canDelete) return null;
    const r = state.blockReasons[0];
    switch (r) {
      case 'geo_sysadmin_only':
        return t('lifecycle.blocked.geoSysadminOnly');
      case 'not_authorized':
        return t('lifecycle.blocked.notAuthorized');
      case 'has_children':
        return t('lifecycle.blocked.hasChildren', { count: state.childCount });
      case 'is_guardian':
        return t('lifecycle.blocked.isGuardian', { count: state.guardedCount });
      case 'has_content':
        return t('lifecycle.blocked.hasContent', { count: state.contentCounts.total });
      case 'already_deleted':
        return t('lifecycle.blocked.alreadyDeleted');
      default:
        return null;
    }
  }, [state, t]);

  const run = useCallback(async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      setDialog(null);
      setConfirmName('');
      await load();
    } catch (e: any) {
      setError(e?.message || t('lifecycle.actionFailed'));
    } finally {
      setBusy(false);
    }
  }, [load, t]);

  if (loading) return <div className={styles.muted}>{t('lifecycle.loading')}</div>;
  if (!state) return <div className={styles.error}>{error ?? t('lifecycle.loadFailed')}</div>;

  const isDissolved = state.state === 'dissolved';

  return (
    <div className={styles.root}>
      {/* ── Current state ── */}
      <div className={styles.stateRow}>
        <span className={isDissolved ? styles.badgeDissolved : styles.badgeActive}>
          {isDissolved ? t('lifecycle.state.dissolved') : t('lifecycle.state.active')}
        </span>
        {isDissolved && state.dissolvedAt && (
          <span className={styles.muted}>
            {t('lifecycle.dissolvedOn', { date: new Date(state.dissolvedAt).toLocaleDateString() })}
          </span>
        )}
      </div>

      {/* ── Dissolve / Reactivate — the prominent, safe action ── */}
      <div className={styles.block}>
        <div className={styles.blockTitle}>
          {isDissolved ? t('lifecycle.reactivate.title') : t('lifecycle.dissolve.title')}
        </div>
        <p className={styles.blockBody}>
          {isDissolved ? t('lifecycle.reactivate.body') : t('lifecycle.dissolve.body')}
        </p>
        <button
          type="button"
          className={styles.btnPrimary}
          disabled={busy || disabledAll || (isDissolved ? !state.canReactivate : !state.canDissolve)}
          onClick={() => setDialog({ kind: isDissolved ? 'reactivate' : 'dissolve' })}
        >
          {isDissolved ? t('lifecycle.reactivate.action') : t('lifecycle.dissolve.action')}
        </button>
      </div>

      {/* ── Delete — the quiet one behind it. Disabled-and-explained. ── */}
      <div className={styles.blockDanger}>
        <div className={styles.blockTitle}>{t('lifecycle.delete.title')}</div>
        <p className={styles.blockBody}>{t('lifecycle.delete.body')}</p>

        {blockedMessage && (
          <div className={styles.blockedReason} role="note">
            {blockedMessage}
          </div>
        )}

        <button
          type="button"
          className={styles.btnDanger}
          disabled={busy || disabledAll || !state.canDelete}
          title={blockedMessage ?? undefined}
          onClick={() => setDialog({ kind: 'delete' })}
        >
          {t('lifecycle.delete.action')}
        </button>
      </div>

      {/* ── Trash ── */}
      {trash.length > 0 && (
        <div className={styles.block}>
          <div className={styles.blockTitle}>{t('lifecycle.trash.title')}</div>
          <p className={styles.blockBody}>{t('lifecycle.trash.body')}</p>
          <ul className={styles.trashList}>
            {trash.map((d) => (
              <li key={d.entityGuid} className={styles.trashRow}>
                <div className={styles.trashInfo}>
                  <span className={styles.trashName}>{d.name}</span>
                  <span className={styles.muted}>
                    {t('lifecycle.trash.daysRemaining', { count: d.daysRemaining })}
                  </span>
                </div>
                <div className={styles.trashActions}>
                  <button
                    type="button"
                    className={styles.btnSecondary}
                    disabled={busy}
                    onClick={() => void run(() => restoreCommunity(d.entityGuid))}
                  >
                    {t('lifecycle.trash.restore')}
                  </button>
                  <button
                    type="button"
                    className={styles.btnDanger}
                    disabled={busy}
                    onClick={() => { setConfirmName(''); setDialog({ kind: 'purge', target: d }); }}
                  >
                    {t('lifecycle.trash.purge')}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {error && <div className={styles.error}>{error}</div>}

      {/* ── Confirm dialogs ──
          Overlay backgrounds are OPAQUE (composite --bg-primary + --bg-elevated).
          --bg-surface is a transparent tint and would produce a see-through
          modal over the settings content behind it. */}
      {dialog && (
        <div className={styles.overlay} onClick={() => !busy && setDialog(null)}>
          <div className={styles.dialog} onClick={(e) => e.stopPropagation()}>
            {dialog.kind === 'dissolve' && (
              <>
                <div className={styles.dialogTitle}>{t('lifecycle.dissolve.confirmTitle')}</div>
                <p className={styles.dialogBody}>{t('lifecycle.dissolve.confirmBody')}</p>
                <div className={styles.dialogActions}>
                  <button type="button" className={styles.btnSecondary} disabled={busy}
                          onClick={() => setDialog(null)}>
                    {t('lifecycle.cancel')}
                  </button>
                  <button type="button" className={styles.btnPrimary} disabled={busy}
                          onClick={() => void run(() => dissolveCommunity(communityGuid))}>
                    {t('lifecycle.dissolve.action')}
                  </button>
                </div>
              </>
            )}

            {dialog.kind === 'reactivate' && (
              <>
                <div className={styles.dialogTitle}>{t('lifecycle.reactivate.confirmTitle')}</div>
                <p className={styles.dialogBody}>{t('lifecycle.reactivate.confirmBody')}</p>
                <div className={styles.dialogActions}>
                  <button type="button" className={styles.btnSecondary} disabled={busy}
                          onClick={() => setDialog(null)}>
                    {t('lifecycle.cancel')}
                  </button>
                  <button type="button" className={styles.btnPrimary} disabled={busy}
                          onClick={() => void run(() => reactivateCommunity(communityGuid))}>
                    {t('lifecycle.reactivate.action')}
                  </button>
                </div>
              </>
            )}

            {dialog.kind === 'delete' && (
              <>
                <div className={styles.dialogTitle}>{t('lifecycle.delete.confirmTitle')}</div>
                <p className={styles.dialogBody}>{t('lifecycle.delete.confirmBody')}</p>
                <div className={styles.dialogActions}>
                  <button type="button" className={styles.btnSecondary} disabled={busy}
                          onClick={() => setDialog(null)}>
                    {t('lifecycle.cancel')}
                  </button>
                  <button type="button" className={styles.btnDanger} disabled={busy}
                          onClick={() => void run(() => softDeleteCommunity(communityGuid))}>
                    {t('lifecycle.delete.action')}
                  </button>
                </div>
              </>
            )}

            {dialog.kind === 'purge' && (
              <>
                <div className={styles.dialogTitle}>{t('lifecycle.purge.confirmTitle')}</div>
                <p className={styles.dialogBody}>
                  {t('lifecycle.purge.confirmBody', { name: dialog.target.name })}
                </p>
                {/* The ONE strong gate (§6). Type the name. */}
                <label className={styles.dialogLabel} htmlFor="l4e-purge-confirm">
                  {t('lifecycle.purge.typeName', { name: dialog.target.name })}
                </label>
                <input
                  id="l4e-purge-confirm"
                  className={styles.input}
                  value={confirmName}
                  onChange={(e) => setConfirmName(e.target.value)}
                  autoComplete="off"
                  disabled={busy}
                />
                <div className={styles.dialogActions}>
                  <button type="button" className={styles.btnSecondary} disabled={busy}
                          onClick={() => setDialog(null)}>
                    {t('lifecycle.cancel')}
                  </button>
                  <button
                    type="button"
                    className={styles.btnDanger}
                    disabled={
                      busy ||
                      confirmName.trim().toUpperCase() !== dialog.target.name.trim().toUpperCase()
                    }
                    onClick={() => void run(() =>
                      purgeCommunity(dialog.target.entityGuid, confirmName.trim()))}
                  >
                    {t('lifecycle.purge.action')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CommunityLifecycleSection;
